import React, { useEffect, useMemo, useState } from "react";
import "./BathroomDashboard.css";


const STATUS_API = "/api/bathroom/status";
const FAN_API = "/api/bathroom/fan";


function getState(data, key) {
  return data?.[key]?.state ?? null;
}


function binaryText(state, onText, offText) {
  if (state === "on") return onText;
  if (state === "off") return offText;
  if (state === "unavailable") return "사용 불가";
  if (state === "unknown") return "알 수 없음";

  return state || "-";
}


async function readJsonResponse(response) {
  const text = await response.text();

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(
      `JSON이 아닌 응답입니다. HTTP ${response.status}\n${text.slice(0, 200)}`
    );
  }
}


export default function BathroomDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [controlling, setControlling] = useState(false);

  const temperature = useMemo(
    () => getState(data, "temperature"),
    [data]
  );

  const humidity = useMemo(
    () => getState(data, "humidity"),
    [data]
  );

  const motion = useMemo(
    () => getState(data, "motion"),
    [data]
  );

  const connection = useMemo(
    () => getState(data, "connection"),
    [data]
  );

  const fan = useMemo(
    () => getState(data, "fan"),
    [data]
  );

  const wifi = useMemo(
    () => getState(data, "wifi"),
    [data]
  );

  const uptime = useMemo(
    () => getState(data, "uptime"),
    [data]
  );

  const humidityOverride = useMemo(
    () => getState(data, "humidity_override"),
    [data]
  );

  const timer = useMemo(
    () => getState(data, "timer"),
    [data]
  );


  const loadStatus = async () => {
    try {
      const response = await fetch(STATUS_API, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
        headers: {
          Accept: "application/json",
        },
      });

      const result = await readJsonResponse(response);

      if (!response.ok || !result.success) {
        throw new Error(
          result.message || `상태 조회 실패: HTTP ${response.status}`
        );
      }

      setData(result.data);
      setError("");

    } catch (err) {
      console.error(err);
      setError(err.message || "욕실 상태 조회 중 오류가 발생했습니다.");

    } finally {
      setLoading(false);
    }
  };


  const controlFan = async (action) => {
    setControlling(true);

    try {
      const response = await fetch(FAN_API, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          action,
        }),
      });

      const result = await readJsonResponse(response);

      if (!response.ok || !result.success) {
        throw new Error(
          result.message || `환풍기 제어 실패: HTTP ${response.status}`
        );
      }

      setError("");
      await loadStatus();

    } catch (err) {
      console.error(err);
      setError(err.message || "환풍기 제어 중 오류가 발생했습니다.");

    } finally {
      setControlling(false);
    }
  };


  useEffect(() => {
    loadStatus();

    const timerId = window.setInterval(
      loadStatus,
      5000
    );

    return () => {
      window.clearInterval(timerId);
    };
  }, []);


  return (
    <div className="bathroom-page">
      <div className="bathroom-container">
        <div className="bathroom-header">
          <div>
            <h1>욕실 환경 제어</h1>
            <p>ESP32 · Home Assistant 연동</p>
          </div>

          <button
            type="button"
            className="bathroom-refresh-button"
            onClick={loadStatus}
            disabled={loading}
          >
            새로고침
          </button>
        </div>

        {error && (
          <div className="bathroom-error">
            {error}
          </div>
        )}

        <div className="bathroom-grid">
          <div className="bathroom-card">
            <span className="bathroom-label">온도</span>
            <strong className="bathroom-value">
              {temperature && temperature !== "unavailable"
                ? `${temperature}°C`
                : "-°C"}
            </strong>
          </div>

          <div className="bathroom-card">
            <span className="bathroom-label">습도</span>
            <strong className="bathroom-value">
              {humidity && humidity !== "unavailable"
                ? `${humidity}%`
                : "-%"}
            </strong>
          </div>

          <div className="bathroom-card">
            <span className="bathroom-label">움직임</span>
            <strong className="bathroom-value bathroom-value-small">
              {binaryText(
                motion,
                "감지됨",
                "감지 없음"
              )}
            </strong>
          </div>

          <div className="bathroom-card">
            <span className="bathroom-label">ESP32 연결</span>
            <strong className="bathroom-value bathroom-value-small">
              {binaryText(
                connection,
                "연결됨",
                "연결 끊김"
              )}
            </strong>
          </div>

          <div className="bathroom-card bathroom-card-wide">
            <span className="bathroom-label">환풍기</span>

            <strong className="bathroom-value bathroom-value-small">
              {binaryText(
                fan,
                "켜짐",
                "꺼짐"
              )}
            </strong>

            <div className="bathroom-controls">
              <button
                type="button"
                className="bathroom-on-button"
                onClick={() => controlFan("turn_on")}
                disabled={controlling}
              >
                켜기
              </button>

              <button
                type="button"
                className="bathroom-off-button"
                onClick={() => controlFan("turn_off")}
                disabled={controlling}
              >
                끄기
              </button>
            </div>
          </div>

          <div className="bathroom-card">
            <span className="bathroom-label">Wi-Fi</span>
            <strong className="bathroom-value bathroom-value-small">
              {wifi && wifi !== "unavailable"
                ? `${wifi} dBm`
                : "-"}
            </strong>
          </div>

          <div className="bathroom-card">
            <span className="bathroom-label">가동 시간</span>
            <strong className="bathroom-value bathroom-value-small">
              {uptime && uptime !== "unavailable"
                ? uptime
                : "-"}
            </strong>
          </div>

          <div className="bathroom-card">
            <span className="bathroom-label">습도 우선 운전</span>
            <strong className="bathroom-value bathroom-value-small">
              {binaryText(
                humidityOverride,
                "활성",
                "비활성"
              )}
            </strong>
          </div>

          <div className="bathroom-card">
            <span className="bathroom-label">타이머</span>
            <strong className="bathroom-value bathroom-value-small">
              {timer && timer !== "unavailable"
                ? timer
                : "-"}
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
}
